import type { LabelBitmap } from './api';

/** A physical printer is remembered per template on this browser/app only. */
const lastProfileKey = (templateId: string) => `storageapp.labels.last-profile.${templateId}`;
const deviceKey = (profileId: string) => `storageapp.labels.printer-device.${profileId}`;

export function loadLastPrinterProfile(templateId: string): string | null {
  try { return localStorage.getItem(lastProfileKey(templateId)); } catch { return null; }
}

export function saveLastPrinterProfile(templateId: string, profileId: string): void {
  try { localStorage.setItem(lastProfileKey(templateId), profileId); } catch { /* storage is optional */ }
}

export function loadLocalPrinterDevice(profileId: string): string {
  try { return localStorage.getItem(deviceKey(profileId)) ?? ''; } catch { return ''; }
}

export function saveLocalPrinterDevice(profileId: string, deviceId: string): void {
  try { localStorage.setItem(deviceKey(profileId), deviceId); } catch { /* pairing remains usable */ }
}

/** Technical polarity makes the device normal; job inversion changes only this run. */
export function effectiveInversion(polarityCorrection: boolean, jobInverted: boolean): boolean {
  return polarityCorrection !== jobInverted;
}

/** Rotate the server raster for a one-off job without mutating its template/profile. */
export async function rotateLabelBitmap(source: LabelBitmap, degrees: 0 | 90 | 180 | 270): Promise<LabelBitmap> {
  if (degrees === 0) return source;
  const image = await createImageBitmap(source.blob);
  const canvas = document.createElement('canvas');
  const swapped = degrees === 90 || degrees === 270;
  canvas.width = swapped ? image.height : image.width;
  canvas.height = swapped ? image.width : image.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas_unavailable');
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((degrees * Math.PI) / 180);
  context.drawImage(image, -image.width / 2, -image.height / 2);
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error('canvas_encode_failed')), 'image/png'));
  const areas = source.qrAreas.map((area) => {
    if (area.unit !== 'px') return area;
    if (degrees === 90) return { ...area, x: image.height - area.y - area.h, y: area.x, w: area.h, h: area.w };
    if (degrees === 180) return { ...area, x: image.width - area.x - area.w, y: image.height - area.y - area.h };
    return { ...area, x: area.y, y: image.width - area.x - area.w, w: area.h, h: area.w };
  });
  return { blob, qrAreas: areas };
}
